import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  getUserByEmail,
  ClientContactDTO,
  DncDetailsDTO,
  bulkUpdateContactOwner,
  bulkUpdateContactStatus,
  bulkUpdateContactDncDetails,
  bulkAssignContactUsers,
  BulkContactUpdateDTO,
} from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsDialog,
  WsTypography,
  WsTypoVariants,
  WsSelect,
  WsDrawer,
} from 'wisestep-base';

import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import ClientUserList from '../../../features/clientDetails/components/clientSharing/ClientUserList';
import DataPrivacy from '../../../features/conversationWidget/components/DataPrivacy';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { limitedPrivacyOptions } from '../../clientDetails/helper/clientDetailsHelper';
import { BulkUpdateType, labels, titles } from '../helper/ContactListingHelper';

interface BulkContactUpdateDialogProps {
  open: boolean;
  updateType: BulkUpdateType;
  selectedContacts: ClientContactDTO[];
  onClose: () => void;
  onBulkUpdate: () => void;
}

const BulkContactUpdateDialog = ({
  open,
  updateType,
  selectedContacts,
  onClose,
  onBulkUpdate,
}: BulkContactUpdateDialogProps) => {
  const [isLoading, setLoading] = useState(false);
  const formMethods = useForm();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const getSelectedContactIds = () => selectedContacts.map((contact) => contact.id!);

  const handleBulkSubmit = async (apiCall, successMessage: string) => {
    setLoading(true);
    try {
      await apiCall();
      setNotification(success(successMessage));
      onBulkUpdate();
      onClose();
    } catch (error) {
      setNotification(failed('Something went wrong, Please try again!'));
    } finally {
      setLoading(false);
    }
  };

  const handleOwnerSubmit = async (data: any) => {
    if (!data.recruiterIds?.[0]) {
      setNotification(warning('Select a Valid Active User'));
      return;
    }
    const recruiter = (await getUserByEmail(data.recruiterIds[0])).data;
    const bulkUpdateDTO: BulkContactUpdateDTO = {
      contactIds: getSelectedContactIds(),
      owner: {
        email: recruiter?.email || '',
        name: `${recruiter?.firstName || ''} ${recruiter?.lastName || ''}`.trim(),
      },
    };
    handleBulkSubmit(
      () => bulkUpdateContactOwner(bulkUpdateDTO),
      'Contact Owners Updated Successfully!'
    );
  };

  const handleStatusSubmit = (data: { markStatus: boolean }) => {
    const bulkUpdateDTO: BulkContactUpdateDTO = {
      contactIds: getSelectedContactIds(),
      markStatus: data.markStatus,
    };
    handleBulkSubmit(
      () => bulkUpdateContactStatus(bulkUpdateDTO),
      `Contacts marked as ${data.markStatus ? 'Active' : 'Inactive'} successfully!`
    );
  };

  const handleAssignUsersSubmit = (data: { assignedUsers: any[] }) => {
    if (!data.assignedUsers || data.assignedUsers.length === 0) {
      setNotification(warning('Please select at least one user'));
      return;
    }
    const userIds = data.assignedUsers.map((item) => item.value ?? item);
    const bulkUpdateDTO: BulkContactUpdateDTO = {
      contactIds: getSelectedContactIds(),
      assignedUsers: userIds,
    };
    handleBulkSubmit(
      () => bulkAssignContactUsers(bulkUpdateDTO),
      'Users assigned to contacts successfully!'
    );
  };

  const handleDncSubmit = async (dncDetails: DncDetailsDTO) => {
    const bulkUpdateDTO: BulkContactUpdateDTO = {
      contactIds: getSelectedContactIds(),
      dncDetails,
    };
    await handleBulkSubmit(
      () => bulkUpdateContactDncDetails(bulkUpdateDTO),
      'Contact DNC Details Updated Successfully!'
    );
  };

  const onSubmit = (data: any) => {
    switch (updateType) {
      case 'owner':
        handleOwnerSubmit(data);
        break;
      case 'status':
        handleStatusSubmit(data);
        break;
      case 'assignUsers':
        handleAssignUsersSubmit(data);
        break;
      default:
        break;
    }
  };

  const renderFormContent = () => {
    switch (updateType) {
      case 'owner':
        return <RecruiterSuggestions multiple={false} name="recruiterIds" required />;
      case 'status':
        return (
          <WsSelect
            label=""
            name="markStatus"
            selectArray={[
              { label: 'Active', value: 'true' },
              { label: 'Inactive', value: 'false' },
            ]}
            size="small"
          />
        );
      case 'assignUsers':
        return (
          <ClientUserList
            clientContactIds={getSelectedContactIds()}
            onAssign={() => {
              onBulkUpdate();
              onClose();
            }}
            moduleName="CLIENT-CONTACT"
          />
        );
      case 'dnc':
        return (
          <DataPrivacy
            email={''}
            candidateName={`${selectedContacts.length} selected contacts`}
            isInDnc={false}
            dncDetails={null}
            privacyOptions={limitedPrivacyOptions}
            restrictLabel="DNC Up to *"
            entityId=""
            parentEntitiyId=""
            entityName=""
            onSubmitApi={handleDncSubmit}
            enableActivityLog={false}
          />
        );
      default:
        return null;
    }
  };

  const renderDrawer = (title?: string, size?: 'small' | 'medium') => (
    <WsDrawer
      open={open}
      onClose={onClose}
      title={title}
      headerSize="medium"
      size={size ?? 'small'}
    >
      <Box sx={{ p: 2 }}>{renderFormContent()}</Box>
    </WsDrawer>
  );

  if (updateType === 'dnc') return renderDrawer('DNC Details', 'medium');
  if (updateType === 'assignUsers') return renderDrawer('Contact Access', 'small');

  return (
    <WsDialog onClose={onClose} openDialog={open}>
      <FormProvider {...formMethods}>
        <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <DialogTitle>{titles[updateType]}</DialogTitle>
            <IconButton onClick={onClose} sx={{ color: WsColors.GreyBlack, mr: 2 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider orientation="horizontal" />
          <Box mt={2} ml={7}>
            <WsTypography variant={WsTypoVariants.BODY1}>{labels[updateType]}</WsTypography>
          </Box>
          <DialogContent sx={{ mb: 2, pl: 7, width: '80%' }}>{renderFormContent()}</DialogContent>
          <Divider orientation="horizontal" />
          <DialogActions sx={{ mr: 2, mb: 1 }}>
            <WsButton
              type="submit"
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </WsButton>
          </DialogActions>
        </Box>
      </FormProvider>
    </WsDialog>
  );
};

export default BulkContactUpdateDialog;
